import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { trackCtaClick } from "../../lib/analytics";
import {
  MarketingShellLayout,
  ValueGrid,
  type ValueItem,
} from "../../components/marketing/MarketingShell";
import { Seo } from "../../seo/Seo";

const VALUES: ValueItem[] = [
  {
    tag: "CONNECT",
    title: "Point at GitHub.",
    body: "Connect a repo. Pick a tag, a date range, or commits since the last release. We pull the PRs and the customer threads they linked.",
  },
  {
    tag: "DRAFT",
    title: "Read by an editor, not a robot.",
    body: "AI drafts one release with multiple framings — plain for customers, operational for CS, outcomes for execs. Tone is set per audience.",
  },
  {
    tag: "DELIVER",
    title: "Email, Slack, in-app — same release.",
    body: "Reusable audiences. Quiet hours. Replies route back to the author. Open rates tracked. Reply rates tracked too — they matter more.",
  },
];

const FRAMINGS = [
  {
    who: "Customers",
    body: "invoices generate in under a second on huge accounts.",
    color: "var(--color-signal-red)",
  },
  {
    who: "CS team",
    body: 'the "processing forever" ticket is gone. Macros updated.',
    color: "var(--color-signal-ok)",
  },
  {
    who: "Sales / PM",
    body: "unblocks the Q2 enterprise deal.",
    color: "var(--color-signal-lavender)",
  },
  {
    who: "Engineering",
    body: "migrated to InvoiceWorker. Structured logs. Rollback flag staged.",
    color: "var(--color-signal-warn)",
  },
];

export function ReleasesMarketingPage() {
  return (
    <MarketingShellLayout active="Releases">
      <Seo route="/releases" />
      {/* Hero */}
      <section>
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 pt-12 sm:pt-16 pb-10 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-14 items-start">
          <div>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] uppercase text-signal-red">
              <span className="size-1.5 rounded-sm bg-signal-red" />
              01 · Releases · for the boring side of launching
            </span>
            <h1 className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-semibold leading-[0.96] tracking-[-0.04em]">
              Release notes{" "}
              <span className="font-serif italic font-normal">your CEO</span>
              <br />
              <span className="text-signal-red">will actually read.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl font-serif">
              Point Swisstools at your GitHub repo. We draft the release — one
              note, several framings: customers, CS, executives, your engineers.
              You edit. We deliver to email and Slack.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 items-center">
              <Link
                to="/register"
                onClick={() =>
                  trackCtaClick("hero", {
                    target: "register",
                    surface: "Releases",
                  })
                }
              >
                <Button size="lg">Start free →</Button>
              </Link>
              <a
                href="/#playground"
                onClick={() =>
                  trackCtaClick("sub_nav", { surface: "Releases" })
                }
                className="text-sm font-medium underline underline-offset-4 decoration-rule hover:decoration-foreground"
              >
                Try the live demo →
              </a>
            </div>
          </div>

          {/* Hero card — release composition */}
          <div className="self-start w-full">
            <div className="bg-card-paper border border-rule rounded-xl overflow-hidden shadow-[0_18px_42px_-22px_rgba(27,31,42,0.18)]">
              <div className="px-4 py-3 border-b border-rule-soft flex items-center justify-between bg-panel">
                <div className="flex items-center gap-2">
                  <span className="size-1.5 rounded-sm bg-signal-red" />
                  <span className="font-mono text-[10.5px] tracking-[0.1em] uppercase text-muted-foreground">
                    v2.18.0 · checkout 2.0
                  </span>
                </div>
                <span className="font-mono text-[10px] text-signal-warn">
                  ● draft
                </span>
              </div>
              <div className="px-5 pt-5 pb-4">
                <div className="text-2xl font-semibold tracking-tight mb-2">
                  Faster invoices, calmer Mondays.
                </div>
                <div className="text-[12.5px] leading-relaxed text-muted-foreground mb-3.5 font-serif italic">
                  Drafted from 17 commits across 4 PRs. AI suggested light
                  edits.
                </div>
                <div className="border-t border-rule-soft pt-3.5 flex flex-col gap-2.5">
                  {FRAMINGS.map((s) => (
                    <div
                      key={s.who}
                      className="flex gap-2.5 text-[12.5px] leading-relaxed"
                    >
                      <span
                        className="font-mono text-[10px] font-semibold shrink-0 pt-0.5 min-w-[90px]"
                        style={{ color: s.color }}
                      >
                        {s.who}
                      </span>
                      <span>{s.body}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-2.5 border-t border-rule-soft flex justify-between items-center bg-panel">
                <span className="font-mono text-[10.5px] text-faint">
                  → 412 emails · #launches · #cs-team
                </span>
                <span className="text-xs text-signal-red font-semibold">
                  Send →
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ValueGrid items={VALUES} accent="var(--color-signal-red)" />

      {/* The difference — pull quote */}
      <section className="border-t border-rule bg-card-paper">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-8 sm:py-12">
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-faint mb-3">
            The difference
          </div>
          <div className="grid md:grid-cols-2 gap-7 md:gap-12">
            <div>
              <div className="font-serif text-[17px] font-semibold mb-1.5">
                A changelog says <span className="italic">what</span> shipped.
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground m-0">
                "Bumped pdf-renderer to 3.4. Added{" "}
                <code className="font-mono bg-panel px-1 rounded">
                  --stream
                </code>{" "}
                flag. Removed dead code."
              </p>
            </div>
            <div className="md:pl-12 md:border-l border-rule">
              <div className="font-serif text-[17px] font-semibold mb-1.5">
                A release says{" "}
                <span className="italic text-signal-red">
                  why anyone should care
                </span>
                .
              </div>
              <p className="text-[13px] leading-relaxed text-muted-foreground m-0">
                "Invoices now generate in under a second, even on accounts with
                thousands of line items. The processing spinner, mostly,
                history."
              </p>
            </div>
          </div>
        </div>
      </section>
    </MarketingShellLayout>
  );
}
